import { useState } from "react";
import Logo from "@/components/Logo";
import FilterSheet from "@/components/FilterSheet";
import RequestForm from "@/components/RequestForm";
import IntentSheet from "@/components/IntentSheet";
import ThemeToggle from "@/components/ThemeToggle";
import Feed from "@/pages/Feed";
import Grid from "@/pages/Grid";
import { Filter, LayoutGrid, Rows3, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import T from "@/lib/testIds";

const DEFAULT_FILTERS = { q: "", kategori: "Semua", stock_status: "", harga_max: "" };

/* ── Button config — single source of truth ─────────────────── */
const BTN = 44;        // header button size (px)
const BTN_GAP = 8;     // gap between header buttons (px)

export default function PublicHome() {
  const [view, setView] = useState("feed");
  const [filterOpen, setFilterOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [intentProduct, setIntentProduct] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [shuffleKey, setShuffleKey] = useState(0);

  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/settings/public")).data,
    staleTime: 5 * 60 * 1000,
  });

  const hasFilter =
    filters.q ||
    (filters.kategori && filters.kategori !== "Semua") ||
    filters.stock_status ||
    filters.harga_max;

  const isFeed = view === "feed";

  // Shared button base style
  const headerBtn = isFeed
    ? "bg-white/12 backdrop-blur-md border-white/20 text-white"
    : "bg-white/70 dark:bg-plum/40 backdrop-blur border-plum/10 text-plum dark:text-cream";

  return (
    <div className={`relative w-full overflow-hidden ${isFeed ? "bg-cream dark:bg-plum-700" : "bg-background"}`}>

      {/* ── Decorative rings (grid only) ──────────────────────── */}
      {view === "grid" && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute top-40 right-8 w-32 h-32 rounded-full border border-sakura-300/25" />
          <div className="absolute top-52 right-2 w-20 h-20 rounded-full border border-lilac-400/20" />
          <div className="absolute top-[520px] -left-8 w-40 h-40 rounded-full border border-babyblue-400/20" />
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-40">
        <div
          className={`${isFeed ? "bg-gradient-to-b from-black/72 via-black/22 to-transparent" : "glass border-b border-plum/10"} px-4 pt-[calc(env(safe-area-inset-top)+10px)] pb-2.5 flex items-center justify-between safe-left safe-right animate-header-in`}
        >
          <Logo compact={isFeed} light={isFeed} onRefresh={() => setShuffleKey((k) => k + 1)} />

          <div className="flex items-center" style={{ gap: `${BTN_GAP}px` }}>

            {/* Request form — always visible */}
            <button
              onClick={() => setRequestOpen(true)}
              aria-label="Titip barang"
              className={`rounded-full grid place-items-center border active:scale-90 transition-transform ${headerBtn}`}
              style={{ height: `${BTN}px`, width: `${BTN}px` }}
            >
              <Plus className="h-4 w-4" strokeWidth={2.2} />
            </button>

            {/* Theme toggle — always visible */}
            <div
              className={`rounded-full grid place-items-center border active:scale-90 transition-transform ${isFeed ? "bg-white/12 backdrop-blur-md border-white/20" : "bg-white/70 dark:bg-plum/40 backdrop-blur border-plum/10"}`}
              style={{ height: `${BTN}px`, width: `${BTN}px` }}
            >
              <ThemeToggle variant={isFeed ? "onDark" : "onLight"} />
            </div>

            {/* Filter */}
            <button
              onClick={() => setFilterOpen(true)}
              data-testid={T.filterOpen}
              aria-label="Filter & Search"
              className={`relative rounded-full grid place-items-center border active:scale-90 transition-transform ${headerBtn}`}
              style={{ height: `${BTN}px`, width: `${BTN}px` }}
            >
              <Filter className="h-4 w-4" strokeWidth={2} />
              {hasFilter && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-sakura-500 border-2 border-background" />
              )}
            </button>

            {/* Grid/Feed toggle */}
            <button
              onClick={() => setView((v) => (v === "feed" ? "grid" : "feed"))}
              data-testid={T.toggleView}
              aria-label="Toggle grid/feed view"
              className={`rounded-full grid place-items-center border active:scale-90 transition-transform ${isFeed ? "bg-sakura-400 border-white/20 text-plum" : "bg-sakura-400 border-plum/10 text-plum shadow-[0_4px_14px_rgba(245,169,199,0.45)]"}`}
              style={{ height: `${BTN}px`, width: `${BTN}px` }}
            >
              {isFeed ? <LayoutGrid className="h-4 w-4" strokeWidth={2} /> : <Rows3 className="h-4 w-4" strokeWidth={2} />}
            </button>

          </div>
        </div>
      </header>

      {/* ── Grid hero banner ──────────────────────────────────── */}
      {view === "grid" && (
        <div className="relative z-10 px-4 pt-[calc(88px+env(safe-area-inset-top))]">
          <div className="rounded-3xl bg-white/70 dark:bg-plum/40 backdrop-blur border border-plum/10 shadow-dreamy p-6 relative overflow-hidden animate-hero-in">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full border border-sakura-300/40" />
            <div className="absolute -right-4 top-4 w-20 h-20 rounded-full border border-lilac-300/40" />
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-sakura-600 dark:text-sakura-300">
              Bangkok · Batch aktif
            </p>
            <h2 className="font-serif italic text-3xl text-plum dark:text-cream mt-2 leading-tight">
              Barang pilihan<br />dari Bangkok.
            </h2>
            <p className="text-xs text-plum/60 dark:text-cream/60 mt-2 leading-relaxed max-w-sm">
              Browsing dulu, tekan simbol hati untuk simpan, atau ketuk{" "}
              <em className="font-serif not-italic">Beli</em> untuk lanjut ke chat admin.
            </p>
          </div>
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────── */}
      {isFeed ? (
        <Feed filters={filters} onOpenIntent={setIntentProduct} shuffleKey={shuffleKey} />
      ) : (
        <Grid filters={filters} onOpenIntent={setIntentProduct} />
      )}

      {/* ── Sheets ────────────────────────────────────────────── */}
      <FilterSheet open={filterOpen} onOpenChange={setFilterOpen} value={filters} onApply={setFilters} />
      <RequestForm open={requestOpen} onOpenChange={setRequestOpen} />
      <IntentSheet
        product={intentProduct}
        brandName={settings?.brand_name || "Zunayya_nitipdong"}
        tripName=""
        onClose={() => setIntentProduct(null)}
      />
    </div>
  );
}
